import { CloseButton } from '@mantine/core';
import type { HTMLAttributes, ReactNode } from 'react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css, useTheme } from 'styled-components';
import { variant } from 'styled-system';
import { TagColorVariant } from '../../constants';
import type { ITheme } from '../../utils/theme';
import { TypographyVariant } from '../../utils/theme';
import { TruncatedText } from '../truncated-text';
import type { TypographyProps } from '../typography/typography';
import Typography from '../typography/typography';

interface IBaseTag extends HTMLAttributes<HTMLParagraphElement> {
  name: string;
  variant?: TagVariant;
  textVariant?: TypographyVariant;
  height?: string;
  padding?: string;
  borderRadius?: string;
  isTruncated?: boolean;
  textAs?: TypographyProps['as'];
  onClickRemove?: () => void;
  icon?: ReactNode;
}

export interface ITag extends IBaseTag {
  fontColor?: string;
  backgroundColor?: string;
  strokeColor?: string;
  colorVariant?: TagColorVariant;
}

interface IStyledTag extends IBaseTag {
  fontColor?: string;
  backgroundColor?: string;
  strokeColor?: string;
}

export enum TagVariant {
  Filled = 'filled',
  Outlined = 'outlined',
}

export function Tag({
  name = '',
  variant = TagVariant.Filled,
  textVariant = TypographyVariant.bodyMd2,
  textAs,
  colorVariant = TagColorVariant.Default,
  isTruncated = false,
  // note: using the below color props will overwrite the colorVariant
  fontColor,
  backgroundColor,
  strokeColor,
  onClickRemove,
  icon,
  ...rest
}: Readonly<ITag>) {
  const theme = useTheme();
  const colorVariantColors = getTagColorVariantColors(colorVariant, theme);
  const TypographyComponent = isTruncated ? TruncatedText : Typography;
  return (
    <StyledTag
      data-testid="tag-container"
      backgroundColor={backgroundColor ?? colorVariantColors.backgroundColor}
      strokeColor={strokeColor ?? colorVariantColors.strokeColor}
      variant={variant}
      isTruncated={isTruncated}
      {...rest}
    >
      {icon && <IconWrapper>{icon}</IconWrapper>}
      <TypographyComponent
        variant={textVariant}
        as={textAs}
        color={fontColor ?? colorVariantColors.fontColor}
      >
        {name}
      </TypographyComponent>
      {onClickRemove && (
        <CloseButton
          onClick={onClickRemove}
          size="sm"
          variant="transparent"
          mr="-6px"
        />
      )}
    </StyledTag>
  );
}

export default Tag;

const baseStyles = ({
  theme,
  height,
  padding,
  isTruncated,
  borderRadius,
}: { theme: ITheme } & Partial<ITag>) => css`
  min-height: ${height ?? '24px'};
  min-width: 0;
  width: fit-content;
  max-width: ${isTruncated ? '100%' : undefined};
  padding: ${padding ?? theme.spacing(0, 1)};
  border-radius: ${borderRadius ?? theme.borderRadius.sm};
  display: flex;
  align-items: center;
  flex: none;

  p {
    margin: 0;
  }
`;

export const getTagColorVariantColors = (
  colorVariant: TagColorVariant,
  theme: ITheme
) => {
  switch (colorVariant) {
    case TagColorVariant.Success: {
      return {
        fontColor: theme.palette.success.main,
        backgroundColor: theme.palette.success.bg,
        strokeColor: theme.palette.success.light,
      };
    }
    case TagColorVariant.Warning: {
      return {
        fontColor: theme.palette.warning.main,
        backgroundColor: theme.palette.warning.bg,
        strokeColor: theme.palette.warning.light,
      };
    }
    case TagColorVariant.Error: {
      return {
        fontColor: theme.palette.error.main,
        backgroundColor: theme.palette.error.bg,
        strokeColor: theme.palette.error.light,
      };
    }
    case TagColorVariant.Neutral: {
      return {
        fontColor: theme.palette.neutral[90],
        backgroundColor: theme.palette.neutral[50],
        strokeColor: theme.palette.neutral[70],
      };
    }
    default: {
      return {
        fontColor: theme.palette.primary.dark,
        backgroundColor: theme.palette.neutral['00'],
        strokeColor: undefined,
      };
    }
  }
};

const variantStyles =
  ({ fontColor, backgroundColor, strokeColor }: Partial<IStyledTag>) =>
  () =>
    variant({
      variants: {
        [TagVariant.Filled]: {
          bg: backgroundColor,
        },
        [TagVariant.Outlined]: {
          bg: backgroundColor,
          border: `1px solid ${strokeColor}`,
        },
      },
    });

const StyledTag: StyledComponent<
  'div',
  DefaultTheme,
  Record<string, unknown> & Partial<IStyledTag>,
  never
> = styled('div')(baseStyles, variantStyles);

const IconWrapper = styled.div`
  margin-right: 4px;
  display: flex;
`;
